import Component from '@glimmer/component';
import { PrismAccordion, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralCountryFlag from 'lumen/components/general/country-flag';
import type { Day } from 'lumen/models';

import styles from './events-and-holidays-list.module.css';

interface HotelPricingAssistantDayDetailTabsEventsAndHolidaysListSignature {
  Args: {
    dayModel: Day;
    wrapDetails?: boolean;
  };
}

export default class HotelPricingAssistantDayDetailTabsEventsAndHolidaysList extends Component<HotelPricingAssistantDayDetailTabsEventsAndHolidaysListSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get eventsAndHolidays() {
    return {
      events: this.args.dayModel.events,
      numberOfEvents: this.args.dayModel.events.length,
      holidays: this.args.dayModel.holidays,
      numberOfHolidays: this.args.dayModel.holidays.length,
    };
  }

  <template>
    {{#if this.eventsAndHolidays.numberOfEvents}}
      <PrismAccordion
        @description={{t
          "pricing_assistant.pricing_tab.events.description"
          count=this.eventsAndHolidays.numberOfEvents
        }}
        @icon="calendar-accepted"
      >
        <div class="flex flex-col gap-400">
          {{#each this.eventsAndHolidays.events as |event|}}
            <div class="flex flex-row gap-200">
              <PrismIcon @name={{event.icon}} @size="small" />
              <div class="{{styles.contents}} flex flex-col gap-200">
                <p class="text-200--bold">{{event.name}}</p>
                <div
                  class="flex flex-row gap-200
                    {{if @wrapDetails styles.details-wrap}}"
                >
                  <div class="{{styles.formatted-days}} flex flex-row gap-100">
                    <PrismIcon @name="calendar-accepted" @size="small" />
                    <p
                      class="text-200--regular"
                    >{{event.formattedDaysInWeekdayAndMonthAndDayMedium}}</p>
                  </div>

                  {{#if event.location}}
                    <div class="flex flex-row gap-100 truncate">
                      <PrismIcon @name="location-pin" @size="small" />
                      <p class="flex flex-row {{styles.location-text}}">
                        <p
                          class="text-200--regular name truncate"
                          title={{event.location}}
                        >
                          {{event.location}}
                        </p>
                        {{#if event.distance}}
                          <p
                            class="text-200--regular {{styles.distance}} ml-50"
                            title={{event.distanceFormatted}}
                          >
                            ({{event.distanceFormatted}})
                          </p>
                        {{/if}}
                      </p>
                    </div>
                  {{/if}}

                  {{#if event.visitors}}
                    <div class="{{styles.visitors}} flex flex-row gap-100">
                      <PrismIcon @name="user-group" @size="small" />
                      <p
                        class="text-200--regular"
                      >{{event.visitorsFormatted}}</p>
                    </div>
                  {{/if}}
                </div>
              </div>
            </div>
          {{/each}}
        </div>
      </PrismAccordion>
    {{/if}}

    {{#if this.eventsAndHolidays.numberOfHolidays}}
      <PrismAccordion
        @description={{t
          "pricing_assistant.pricing_tab.holidays.description"
          count=this.eventsAndHolidays.numberOfHolidays
        }}
        @icon="events-global"
      >
        <div class="flex flex-col gap-400">
          {{#each this.eventsAndHolidays.holidays as |holiday|}}
            <div class="flex flex-row gap-200">
              {{#each holiday.countries as |country|}}
                <GeneralCountryFlag @countryCode={{country}} />
              {{/each}}
              <p>{{holiday.name}}</p>
            </div>
          {{/each}}
        </div>
      </PrismAccordion>
    {{/if}}
  </template>
}
